// =============================================================================
// EXAMPLE of default global navigation guard function
// Its check if the user is logged in, have permission to access the route etc.
// The implementation detail will depend case by cases.
// Please refer to:
// 1. Vue Router Navigation Guard: https://router.vuejs.org/guide/advanced/navigation-guards.html
// 2. OIDC Client Vue Example: https://github.com/joaojosefilho/vuejsOidcClient
// =============================================================================

// app module import
import Mgr from "../../services/securityService";
import appStore from "../../store/appStore";
import constructMaintenancePageGuard from "./maintenancePageGuard";
import jwt_decode from "jwt-decode";

export default function defaultGlobalGuard(to, from, next) {
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const routeName = to.name;
  const mgr = new Mgr(appStore.state.config.configurationData.common_module);

  const checkTokenValidity = async () => {
    // function to check whether access token is expired or not
    const currTime = new Date().getTime();
    const decodedToken = jwt_decode(appStore.state.auth.accessToken);
    const expTime = new Date(decodedToken.exp * 1000).getTime();
    if (expTime < currTime) {
      const isReachMaxAbsoluteSession = () => {
        const toleranceValue = 60; //  to allow the current time to deviate when validating (in seconds)

        return (
          Math.floor(
            (appStore.state.config.configurationData.common_module.commonConfig
              .idpMaxAbsoluteSession +
              decodedToken.auth_time) /
              1000
          ) <
          Math.floor(new Date().getTime() / 1000) + toleranceValue
        );
      };
      await mgr
        .renewToken()
        .then(() => {
          appStore.commit("auth/setTokenCheck", true);
        })
        .catch(() => {
          if (isReachMaxAbsoluteSession() === true) {
            appStore.commit("auth/setIsManuallySignedOut", false);
            mgr?.signOut();
          } else {
            appStore.commit("auth/setIsSessionActive", false);
            appStore.commit("auth/setTokenCheck", true);
          }
        });
    }
    // end check
  };

  const {
    underMaintenancePath,
    isRequiresAuthAppsUnderMaintenance,
    underMaintenanceMessage
  } = constructMaintenancePageGuard();

  const arrOfPath = [];
  if (Array.isArray(to.matched)) {
    if (to.matched.length > 0) {
      to.matched.map(item => {
        if (item.path) arrOfPath.push(item.path);
      });
    }
  }

  if (
    underMaintenancePath.length > 0 &&
    arrOfPath.some(a => underMaintenancePath.includes(a)) &&
    to.name !== "maintenance"
  )
    next({
      name: "maintenance",
      params: { text: underMaintenanceMessage[to.path] }
    });

  // Going straight to Oidc Callback page
  if (to.name === "OidcCallback" && !appStore.state.auth.signedIn) {
    next();
  }

  // Check if page require authentication and it's not login page
  if (requiresAuth && routeName !== "Login") {
    if (isRequiresAuthAppsUnderMaintenance && to.name !== "maintenance")
      next({
        name: "maintenance",
        params: { text: underMaintenanceMessage["/"] }
      });

    if (!appStore.state.auth.signedIn) {
      next({
        path: "/login",
        query: { destination: to.fullPath === "/" ? undefined : to.fullPath }
      });
    } else {
      appStore.commit("auth/setTokenCheck", false);
      mgr.getSignedIn().then(
        async signedIn => {
          if (signedIn) {
            await checkTokenValidity();

            mgr.getUser().then(user => {
              if (!appStore.state.header.isImpersonating) {
                appStore.commit(
                  "header/setAdditionalProfileProperties",
                  user.profile
                );
                appStore.commit(
                  "header/setRoles",
                  typeof user.profile.roles === "string"
                    ? [user.profile.roles]
                    : user.profile.roles
                    ? [...user.profile.roles]
                    : []
                );
                appStore.commit("auth/setAccessToken", user.access_token);
              }

              appStore.commit("auth/setTokenCheck", true);

              if (
                !to.path.includes("/profile-management") &&
                !appStore.state.header.hasProfile
              ) {
                if (to.path.includes("/feedback-management")) {
                  next();
                } else {
                  next({ path: "/profile-management" });
                }
              } else {
                if (!appStore.state.header.userProfile.isValidated) {
                  if (!to.path.includes("/access-denied")) {
                    next({ path: "/access-denied" });
                  } else {
                    next();
                  }
                } else if (
                  appStore.state.header.userProfile.roles.includes(
                    "pii-brokers"
                  ) &&
                  appStore.state.header.userProfile.profileType == 1
                ) {
                  if (!to.path.includes("/PII-management"))
                    next({ path: "/PII-management" });
                  else next();
                } else if (
                  appStore.state.header.userProfile.roles.includes("aea")
                ) {
                  if (
                    !to.path.includes("/RES-exam-management") &&
                    !to.path.includes("/REA-exam-management")
                  )
                    next({ path: "/RES-exam-management" });
                  else next();
                } else if (
                  to.path !== "/survey" &&
                  !appStore.state.survey.hasFilledSurvey &&
                  appStore.state.survey.isSurveyMandatory &&
                  appStore.state.header.hasProfile
                ) {
                  next({ path: "/survey" });
                } else {
                  next();
                }
              }
            });
          }
        },
        () => {} // error handler
      );
    }
  } else if (routeName === "Login" && appStore.state.auth.signedIn) {
    // Login page is accessible if signedIn status is false
    // otherwise going back to previous page
    appStore.commit("auth/setTokenCheck", true);
    next({
      path: from.path,
      params: { nextUrl: from.fullPath }
    });
  } else {
    const isGoToFeedbackManagement = to.path.includes("feedback-management");
    if (isGoToFeedbackManagement && appStore.state.auth.signedIn) {
      appStore.commit("auth/setTokenCheck", false);
      mgr.getSignedIn().then(async () => {
        await checkTokenValidity();
        mgr.getUser().then(user => {
          if (!appStore.state.header.isImpersonating) {
            appStore.commit(
              "header/setAdditionalProfileProperties",
              user.profile
            );
          }

          appStore.commit("auth/setTokenCheck", true);

          next();
        });
      });
    } else {
      if (to.name === "Login" && isRequiresAuthAppsUnderMaintenance) {
        appStore.commit("auth/setTokenCheck", true);
        next({
          name: "maintenance",
          params: { text: underMaintenanceMessage["/"] }
        });
      } else {
        appStore.commit("auth/setTokenCheck", true);
        next();
      }
    }
  }
}
